import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';

import type { PageNotificationProps } from '@coursera/cds-core';

import { ItemStates } from 'bundles/authoring/common/constants';

const { PUBLISHED, READ_ONLY, ERROR, TRANSIENT_ERROR } = ItemStates;

type Props = {
  itemId: string;
  type: string;
  message: JSX.Element | string;
};

// Comment copied from CourseMaterialStore.
// TODO: This is incomplete workaround to type ItemStateStore. Need to create type
// definitions for createStore and finalize this one.
export type ItemStateStoreType = {
  getIsReadOnly: () => boolean;
};

/**
 * Store for managing states in the item authoring view
 * Consumed by the ItemNotification to show the right state for an item
 * @type {FluxibleStore}
 */
class ItemStateStore extends BaseStore {
  declare _itemId: string;

  declare _type: string;

  declare _message: JSX.Element | string;

  declare _isReadOnly: boolean;

  declare emitChange: () => void;

  static storeName = 'ItemStateStore';

  constructor(dispatcher: any) {
    super(dispatcher);
    this.reset();
  }

  reset() {
    this._itemId = '';
    this._type = '';
    this._message = '';
    this._isReadOnly = false;
  }

  // returns a CDS Notification severity based on item state
  getSeverity(): PageNotificationProps['severity'] {
    switch (this._type) {
      case PUBLISHED:
        return 'success';

      case ERROR:
      case TRANSIENT_ERROR:
        return 'error';

      case READ_ONLY:
      default:
        return 'information';
    }
  }

  getMessage(): JSX.Element | string {
    return this._message;
  }

  getIsTransient(): boolean {
    // all 'success' and 'bad_request' states are transient
    return this._type === PUBLISHED || this._type === TRANSIENT_ERROR;
  }

  getIsReadOnly(): boolean {
    return this._isReadOnly;
  }

  getItemId(): string {
    return this._itemId;
  }

  static handlers = {
    RESET_ITEM_STATE: 'onResetItemState',
    RESET_NOTIFICATION_STATE: 'onResetNotificationState',
    SET_TYPE_MESSAGE: 'onSetTypeMessage',
    SET_IS_READ_ONLY: 'onSetIsReadyOnly',
    SET_IS_NOT_READ_ONLY: 'onSetIsNotReadyOnly',
  };

  onResetItemState() {
    // reset whenever a new item loads or we close out of item authoring view
    this.reset();
    this.emitChange();
  }

  onResetNotificationState() {
    this._type = '';
    this._message = '';
    this.emitChange();
  }

  onSetTypeMessage({ type, message, itemId }: Props) {
    if (this._itemId !== itemId) {
      // initial or new item
      this._itemId = itemId;
    }
    if (type === ERROR) {
      this._isReadOnly = true;
    }

    this._type = type;
    this._message = message;

    this.emitChange();
  }

  onSetIsReadyOnly({ itemId }: { itemId: string }) {
    if (this._itemId !== itemId) {
      // initial or new item
      this._itemId = itemId;
    }

    this._isReadOnly = true;
    this.emitChange();
  }

  onSetIsNotReadyOnly() {
    this._isReadOnly = false;
    this.emitChange();
  }
}

export default ItemStateStore;
